<template>
  <div class="feed-body">
    <div class="feed-contents">
      <!-- Header Section -->
      <div class="header-section">
        <div class="header-content">
          <h1 class="main-title">
            <span class="gradient-text">Agency</span>
          </h1>
          <p class="subtitle">Connect with impactful organizations in your community </p>
        </div>
      </div>

      <!-- Premium Tabs -->
      <div class="tabs-container">
        <div class="tabs-wrapper">
          <div 
            class="tab-button" 
            :class="{ 'active': activeTab === 'all' }"
            @click="switchTab('all')"
          >
            <div class="tab-icon">
              <v-icon small>mdi-account-group</v-icon>
            </div>
            <span class="tab-text">All Agencies</span>
            <div class="tab-count">{{ agencies.length }}</div>
          </div>
          
          <div 
            class="tab-button" 
            :class="{ 'active': activeTab === 'following' }"
            @click="switchTab('following')"
          >
            <div class="tab-icon">
              <v-icon small>mdi-account-heart</v-icon>
            </div>
            <span class="tab-text">Following</span>
            <div class="tab-count">{{ getFollowingAgencies.length }}</div>
          </div>
          
          <div 
            class="tab-button" 
            :class="{ 'active': activeTab === 'followers' }"
            @click="switchTab('followers')"
          >
            <div class="tab-icon">
              <v-icon small>mdi-account-multiple-plus</v-icon>
            </div>
            <span class="tab-text">Followers</span>
            <div class="tab-count">{{ getFollowedAgencies.length }}</div>
          </div>
          
          <div class="tab-indicator" :style="indicatorStyle"></div>
          <div class="tab-glow" :style="glowStyle"></div>
        </div>
      </div>

      <!-- Tab Content -->
      <v-fade-transition mode="out-in">
        <!-- All Agencies -->
        <div v-if="activeTab === 'all'" key="all-agencies" class="tab-content">
          <div class="content-header">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              label="Search agencies..."
              outlined
              dense
              hide-details
              class="search-field"
              clearable
            ></v-text-field>
          </div>

          <div class="agencies-grid">
            <transition-group name="card-list" tag="div" class="card-grid">
              <div
                v-for="agency in filteredAgencies"
                :key="agency._id"
                class="card-container"
              >
                <volunteer-agency-card 
                  :agency="agency" 
                  class="agency-card-enhanced"
                />
              </div>
            </transition-group>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
            <p class="loading-text">Loading amazing agencies...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="filteredAgencies.length === 0" class="empty-state">
            <div class="empty-icon">
              <v-icon size="80" color="rgba(255,255,255,0.3)">mdi-account-search</v-icon>
            </div>
            <h3 class="empty-title">No agencies found</h3>
            <p class="empty-subtitle">Try adjusting your search</p>
            <v-btn
              class="cta-button"
              large
              rounded
              gradient
              @click="clearFilters"
            >
              <v-icon left>mdi-refresh</v-icon>
              Reset Search
            </v-btn>
          </div>
        </div>

        <!-- Following Tab -->
        <div v-else-if="activeTab === 'following'" key="following" class="tab-content">
          <div class="agencies-grid">
            <transition-group name="card-list" tag="div" class="card-grid">
              <div
                v-for="agency in getFollowingAgencies"
                :key="agency._id"
                class="card-container"
              >
                <volunteer-agency-card 
                  :agency="agency" 
                  class="agency-card-enhanced"
                />
              </div>
            </transition-group>
          </div>

          <!-- Loading State -->
          <div v-if="loadingFollowing" class="loading-container">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
            <p class="loading-text">Loading who you're following...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="getFollowingAgencies.length === 0" class="empty-state">
            <div class="empty-icon">
              <v-icon size="80" color="rgba(255,255,255,0.3)">mdi-account-plus</v-icon>
            </div>
            <h3 class="empty-title">Not following any agencies yet</h3>
            <p class="empty-subtitle">Discover and follow impactful organizations</p>
            <v-btn
              class="cta-button"
              large
              rounded
              gradient
              @click="switchTab('all')"
            >
              <v-icon left>mdi-compass</v-icon>
              Discover Agencies
            </v-btn>
          </div>
        </div>

        <!-- Followers Tab -->
        <div v-else-if="activeTab === 'followers'" key="followers" class="tab-content">
          <div class="agencies-grid">
            <transition-group name="card-list" tag="div" class="card-grid">
              <div
                v-for="agency in getFollowedAgencies"
                :key="agency._id"
                class="card-container"
              >
                <volunteer-agency-card 
                  :agency="agency" 
                  class="agency-card-enhanced"
                />
              </div>
            </transition-group>
          </div>

          <!-- Loading State -->
          <div v-if="loadingFollowers" class="loading-container">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
            <p class="loading-text">Loading your followers...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="getFollowedAgencies.length === 0" class="empty-state">
            <div class="empty-icon">
              <v-icon size="80" color="rgba(255,255,255,0.3)">mdi-account-heart</v-icon>
            </div>
            <h3 class="empty-title">No followers yet</h3>
            <p class="empty-subtitle">Engage with agencies to build your network</p>
            <v-btn
              class="cta-button"
              large
              rounded
              gradient
              @click="switchTab('all')"
            >
              <v-icon left>mdi-account-search</v-icon>
              Explore Agencies
            </v-btn>
          </div>
        </div>
      </v-fade-transition>
    </div>

    <!-- Snackbar -->
    <v-snackbar 
      v-model="snackbar" 
      :color="snackbarColor"
      shaped
      class="premium-snackbar"
    >
      <div class="snackbar-content">
        <v-icon left>{{ snackbarIcon }}</v-icon>
        {{ errorMessage }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import VolunteerAgencyCard from "./AgencyCard.vue";

export default {
  components: { VolunteerAgencyCard },
  data() {
    return {
      activeTab: 'all',
      loading: false,
      loadingFollowing: false,
      loadingFollowers: false,
      errorMessage: null,
      snackbar: false,
      snackbarColor: 'error',
      snackbarIcon: 'mdi-alert-circle',
      searchQuery: '',
    };
  },
  computed: {
    ...mapGetters("agency", {
      agencies: "getAllAgencies",
      getFollowedAgencies: "getFollowedAgencies",
      getFollowingAgencies: "getFollowingAgencies"
    }),
    filteredAgencies() {
      if (!this.searchQuery) return this.agencies;
      
      return this.agencies.filter(agency => 
        agency.name?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        agency.description?.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    indicatorStyle() {
      const positions = {
        all: '0%',
        following: '33.33%',
        followers: '66.66%'
      };
      return {
        left: positions[this.activeTab],
        width: '33.33%',
        transform: 'translateX(0)'
      };
    },
    glowStyle() {
      const positions = {
        all: '0%',
        following: '33.33%',
        followers: '66.66%'
      };
      return {
        left: positions[this.activeTab],
        width: '33.33%'
      };
    }
  },
  methods: {
    ...mapActions("agency", {
      fetchAllAgencies: "fetchAllAgencies",
      fetchFollowedAgencies: "fetchFollowedAgencies",
      fetchFollowingAgencies: "fetchFollowingAgencies"
    }),
    switchTab(tab) {
      this.activeTab = tab;
      this.searchQuery = '';
      
      if (tab === 'followers') {
        this.loadFollowers();
      } else if (tab === 'following') {
        this.loadFollowing();
      }
    },
    clearFilters() {
      this.searchQuery = '';
    },
    loadFollowing() {
      this.loadingFollowing = true;
      this.fetchFollowingAgencies()
        .then(() => {
          this.loadingFollowing = false;
        })
        .catch(error => {
          this.loadingFollowing = false;
          this.showErrorMessage(error.response?.data?.message || "Failed to load following");
        });
    },
    loadFollowers() {
      this.loadingFollowers = true;
      this.fetchFollowedAgencies()
        .then(() => {
          this.loadingFollowers = false;
        })
        .catch(error => {
          this.loadingFollowers = false;
          this.showErrorMessage(error.response?.data?.message || "Failed to load followers");
        });
    },
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbarColor = 'error';
      this.snackbarIcon = 'mdi-alert-circle';
      this.snackbar = true;
    },
    showSuccessMessage(message) {
      this.errorMessage = message;
      this.snackbarColor = 'success';
      this.snackbarIcon = 'mdi-check-circle';
      this.snackbar = true;
    }
  },
  watch: {
    '$route.query.tab'(newTab) {
      if (["all", "following", "followers"].includes(newTab)) {
        this.activeTab = newTab;
      }
    }
  },
  mounted() {
    const tabFromQuery = this.$route.query.tab;
    if (["all", "following", "followers"].includes(tabFromQuery)) {
      this.activeTab = tabFromQuery;
    }
    
    this.loading = true;
    this.fetchAllAgencies()
      .then(() => {
        this.loading = false;
      })
      .catch(error => {
        this.loading = false;
        this.showErrorMessage(error.response?.data?.message || "Failed to load agencies");
      });
    
    this.loadFollowing();
    this.loadFollowers();
  }
};
</script>

<style scoped>
/* Base Styles */
.feed-body {
  width: 100%;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  /* background: #121212; */
}

.feed-contents {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Header Section */
.header-section {
  margin-bottom: 40px;
  text-align: center;
}

.header-content {
  margin-bottom: 30px;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 16px;
  line-height: 1.2;
  color: white;
}

.gradient-text {
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #ffe66d, #ff6b6b);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 4s ease infinite;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.subtitle {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
}

/* Premium Tabs */
.tabs-container {
  margin-bottom: 40px;
  display: flex;
  justify-content: center;
}

.tabs-wrapper {
  position: relative;
  display: flex;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 6px;
  overflow: hidden;
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 24px;
  cursor: pointer;
  border-radius: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  z-index: 2;
  position: relative;
  white-space: nowrap;
  min-width: 140px;
}

.tab-button:hover {
  color: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.tab-button.active {
  color: white;
}

.tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tab-count {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 700;
  min-width: 35px;
  text-align: center;
}

.tab-button.active .tab-count {
  background: rgba(255, 255, 255, 0.3);
}

.tab-indicator {
  position: absolute;
  top: 6px;
  bottom: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

.tab-glow {
  position: absolute;
  top: 6px;
  bottom: 6px;
  background: linear-gradient(45deg, rgba(255, 107, 107, 0.3), rgba(78, 205, 196, 0.3));
  border-radius: 14px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
  filter: blur(8px);
}

/* Content Sections */
.tab-content {
  animation: fadeInUp 0.6s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-header {
  margin-bottom: 30px;
}

.search-field {
  max-width: 600px;
  margin: 0 auto;
  background: #fff !important;
  border-radius: 23px !important;
  padding: 10px;
}

.search-field >>> .v-input__control {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 12px;
}

.search-field >>> .v-text-field__details {
  display: none;
}

/* Grid Layout */
.agencies-grid {
  width: 100%;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  width: 100%;
}

.card-container {
  display: flex;
  justify-content: center;
}

.agency-card-enhanced {
  width: 100%;
  max-width: 320px;
  transition: all 0.3s ease;
}

/* Animations */
.card-list-enter-active,
.card-list-leave-active {
  transition: all 0.6s ease;
}

.card-list-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.9);
}

.card-list-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.9);
}

.card-list-move {
  transition: transform 0.6s ease;
}

/* Loading States */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 20px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 60px;
  height: 60px;
  top: 10px;
  left: 10px;
  animation-delay: 0.3s;
  border-top-color: rgba(255, 193, 7, 0.8);
}

.spinner-ring:nth-child(3) {
  width: 40px;
  height: 40px;
  top: 20px;
  left: 20px;
  animation-delay: 0.6s;
  border-top-color: rgba(255, 152, 0, 0.8);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  font-weight: 500;
}

/* Empty States */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;
}

.empty-title {
  color: white;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 12px;
}

.empty-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  margin-bottom: 32px;
  max-width: 400px;
}

.cta-button {
  background: linear-gradient(45deg, #FFC107, #FF9800) !important;
  border: none !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
  transition: all 0.3s ease !important;
}

.cta-button:hover {
  transform: translateY(-3px) !important;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4) !important;
}

/* Premium Snackbar */
.premium-snackbar >>> .v-snack__wrapper {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
  }
  
  .tabs-wrapper {
    flex-direction: column;
    gap: 4px;
  }
  
  .tab-button {
    min-width: auto;
    width: 100%;
  }
  
  .tab-indicator,
  .tab-glow {
    display: none;
  }
  
  .card-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .empty-title {
    font-size: 1.5rem;
  }
  
  .empty-subtitle {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .feed-contents {
    padding: 20px 16px;
  }
  
  .main-title {
    font-size: 1.8rem;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  .tab-button {
    padding: 12px 16px;
    font-size: 0.9rem;
  }
}
</style>